/**
* @copyright Copyright (c) MITSUBISHI Electric Corporation. All rights reserved.
*/

import React, { useContext, useState } from "react"
import { IndexContext } from "../IndexProvider"
import { Modal } from "react-bootstrap"
import ROLES from "../consts/roles"
import validate from 'validate.js'
import PAGES from "../consts/pages"

const orgNameConstraints = {
    orgName: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[\S\s]{1,50}$/,
        }
    }
};

const orgIdConstraints = {
    orgId: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[a-zA-Z0-9\s/-]{3,10}$/,
        }
    }
};

export default function TenantList() {
    const [searchField, setSearchField] = useState("byId")
    const [searchText, setSearchText] = useState("")
    const [modalShow, setModalShow] = useState(false)
    const [modalTitle, setModalTitle] = useState("")
    const [orgName, setOrgName] = useState("");
    const [orgId, setOrgId] = useState("")
    const [isOrgNameValid, setIsOrgNameValid] = useState(false)
    const [isOrgIdValid, setIsOrgIdValid] = useState(false)
    const [shouldShowOrgNameFeedback, setShouldShowOrgNameFeedback] = useState(false)
    const [shouldShowOrgIdFeedback, setShouldShowOrgIdFeedback] = useState(false)
    const [isPending, setIsPending] = useState(false)
    const { accountDetails, tenantList, setSelectedTenant, updateTenant, deleteTenant, createTenant, breadcrumbs, setBreadcrumbs, pageLoading, setPageLoading, setCurrentPage, getAccountList } = useContext(IndexContext)

    const onSearchBtnClick = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onClearBtnClick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setSearchText("")
    }

    const onSearchFormSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }


    const createTenantElementList = () => {
        if (!tenantList) return []
        let filteredTenantList = searchText ? tenantList.filter((tenant) => {
            if (searchField === "byId") {
                return tenant.tenant_id.includes(searchText)
            } else {
                return tenant.tenant_name.includes(searchText)
            }
        }) : tenantList.slice()

        const elementList = filteredTenantList && filteredTenantList.map((tenant, idx) => {
            // console.log(tenant)
            return (
                <tr key={idx}>
                    <th scope="row">{idx + 1}</th>
                    <td>{tenant.tenant_id}</td>
                    <td><a style={{ cursor: "pointer" }} onClick={async (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSelectedTenant(tenant)
                        setBreadcrumbs(breadcrumbs.concat([{ title: `アカウント一覧(組織名：${tenant.tenant_name})`, page: PAGES.ACCOUNTS }]))
                        setPageLoading(true)
                        setCurrentPage(PAGES.ACCOUNTS)
                        await getAccountList(tenant.tenant_id)
                        setPageLoading(false)
                    }} >{tenant.tenant_name}</a></td>
                    {
                        accountDetails.tenant_id !== tenant.tenant_id && accountDetails.role === ROLES.SYSTEM_ADMIN ?
                            <td style={{ width: 160 }}>
                                <button className="btn btn-outline-warning me-2" onClick={(e) => {
                                    setModalTitle("組織変更")
                                    setSelectedTenant(tenant)
                                    setModalShow(true)
                                    setOrgId(tenant.tenant_id)
                                    setOrgName(tenant.tenant_name)
                                }} >変更</button>
                                <button className="btn btn-outline-danger" onClick={(e) => {
                                    setModalTitle("組織削除")
                                    setSelectedTenant(tenant)
                                    setOrgId(tenant.tenant_id)
                                    setOrgName(tenant.tenant_name)
                                    setModalShow(true)
                                }} >削除</button>
                            </td> : <td></td>
                    }
                </tr>
            );
        })

        return elementList
    }

    const buildFormControlClasses = (shouldShowFeedback, isValid) => {
        if (shouldShowFeedback) {
            return `form-control ${isValid ? "is-valid" : "is-invalid"}`
        } else {
            return "form-control"
        }
    }

    const onOrgNameChange = (event) => {
        setOrgName(event.target.value)
        setShouldShowOrgNameFeedback(true)
        if (validate({ orgName: event.target.value }, orgNameConstraints)) {
            setIsOrgNameValid(false)
        } else {
            setIsOrgNameValid(true)
        }
    }

    const onOrgIdChange = (event) => {
        setOrgId(event.target.value)
        setShouldShowOrgIdFeedback(true)
        if (validate({ orgId: event.target.value }, orgIdConstraints)) {
            setIsOrgIdValid(false)
        } else {
            setIsOrgIdValid(true)
        }
    }

    const onOrgEditBtnClick = async (e) => {
        setIsPending(true)
        await updateTenant({ tenant_id: orgId, tenant_name: orgName })
        setIsPending(false)
        setModalShow(false)
        setOrgName("")
        setOrgId("")
        setIsOrgIdValid(false)
        setShouldShowOrgIdFeedback(false)
        setIsOrgNameValid(false)
        setShouldShowOrgNameFeedback(false)
    }

    const onOrgDeleteBtnClick = async (e) => {
        setIsPending(true)
        await deleteTenant({ tenant_id: orgId, tenant_name: orgName })
        setIsPending(false)
        setModalShow(false)
        setOrgName("")
        setOrgId("")
        setIsOrgIdValid(false)
        setShouldShowOrgIdFeedback(false)
        setIsOrgNameValid(false)
        setShouldShowOrgNameFeedback(false)
    }

    const onOrgCreateBtnClick = async (e) => {
        setIsPending(true)
        await createTenant({ tenant_id: orgId, tenant_name: orgName })
        setIsPending(false)
        setModalShow(false)
        setOrgName("")
        setOrgId("")
        setIsOrgIdValid(false)
        setShouldShowOrgIdFeedback(false)
        setIsOrgNameValid(false)
        setShouldShowOrgNameFeedback(false)
    }

    const createModalBody = () => {
        if (modalTitle === "組織変更") {
            return (
                <>
                    <div className="mb-3 row">
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="orgNameInput">組織ID</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input readOnly className="form-control-plaintext" value={orgId} id="orgNameInput" type="text" />
                            <div className="invalid-feedback">
                                組織IDを入力してください。
                            </div>
                        </div>

                    </div>
                    <div className="mb-3 row">
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="orgNameInput">組織名</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className={buildFormControlClasses(shouldShowOrgNameFeedback, isOrgNameValid)} onChange={onOrgNameChange} value={orgName} id="orgNameInput" type="text" />
                            <div className="invalid-feedback">
                                組織名を入力してください。
                            </div>
                        </div>
                    </div>
                </>
            )
        } else if (modalTitle === "組織削除") {
            return (
                <div>{`組織ID：${orgId}、組織名：${orgName}を削除します。宜しいですか？`}</div>
            )
        } else if (modalTitle === "組織登録") {
            return (
                <>
                    <div className="mb-3 row">
                        <div className="col-12 col-sm-2 d-flex align-items-center" >
                            <label htmlFor="orgNameInput">組織ID</label>
                        </div>
                        <div className="col-12 col-sm-10 ">
                            <input className={buildFormControlClasses(shouldShowOrgIdFeedback, isOrgIdValid)} onChange={onOrgIdChange} value={orgId} id="orgNameInput" type="text" />
                            <div className="invalid-feedback">
                                組織IDを入力してください。
                            </div>
                        </div>
                    </div>
                    <div className="mb-3 row">
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="orgNameInput">組織名</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className={buildFormControlClasses(shouldShowOrgNameFeedback, isOrgNameValid)} onChange={onOrgNameChange} value={orgName} id="orgNameInput" type="text" />
                            <div className="invalid-feedback">
                                組織名を入力してください。
                            </div>
                        </div>
                    </div>
                </>
            )
        }
    }

    const createModalFooter = () => {
        let actionBtn
        if (modalTitle === "組織変更") {
            actionBtn = isPending ? <button className="btn btn-outline-warning" disabled >保存中...</button> : <button className="btn btn-outline-warning" onClick={onOrgEditBtnClick} >変更を保存</button>
        } else if (modalTitle === "組織削除") {
            actionBtn = isPending ? <button className="btn btn-outline-danger" disabled >削除中...</button> : <button className="btn btn-outline-danger" onClick={onOrgDeleteBtnClick} >削除</button>
        } else if (modalTitle === "組織登録") {
            actionBtn = isPending ? <button className="btn btn-outline-primary" disabled >登録中...</button> : <button className="btn btn-outline-primary" onClick={onOrgCreateBtnClick} >新規登録</button>
        }

        const cancelBtn = isPending ? <button className="btn btn-outline-secondary ms-3" disabled >キャンセル</button> : <button className="btn btn-outline-secondary ms-3" onClick={(e) => setModalShow(false)} >キャンセル</button>

        return (
            <>
                {cancelBtn}
                {actionBtn}
            </>
        )

    }

    const createFormElement = (isSkeleton) => {
        const firstDiv =
            <div className="col-12 col-sm-6 col-md-2 mb-3 mb-md-0" style={{ visibility: isSkeleton ? "hidden" : "visible" }} >
                {
                    isSkeleton ?
                        <select className="form-select"></select>
                        :
                        <select className="form-select" value={searchField} onChange={(e) => {
                            setSearchField(e.target.value)
                        }}>
                            <option value="byId" active="true">組織IDでフィルター</option>
                            <option value="byName">組織名でフィルター</option>
                        </select>
                }
            </div>

        const secondDiv =
            <div className="col-12 col-sm-6 col-md-4 mb-3 mb-md-0" style={{ visibility: isSkeleton ? "hidden" : "visible" }} >
                {
                    isSkeleton ?
                        <input className="form-control" />
                        :
                        <input type="text" className="form-control" id="searchInput" placeholder="組織を洗い出す" value={searchText} onChange={(e) => {
                            setSearchText(e.target.value)
                        }}></input>
                }
            </div>

        const thirdDiv =
            <div className="col-6 col-md-4" style={{ visibility: isSkeleton ? "hidden" : "visible" }} >
                {
                    isSkeleton ?
                        <>
                            <button className="btn btn-primary" >skeleton</button>
                            <button className="btn btn-primary" >skeleton</button>
                        </>
                        :
                        <>
                            <button className="btn btn-outline-primary me-3" onClick={onSearchBtnClick} >フィルター</button>
                            <button className="btn btn-outline-primary" onClick={onClearBtnClick} >クリア</button>
                        </>

                }
            </div>

        const fourthDiv =
            <div className="col-6 col-md-2" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                {
                    isSkeleton ?
                        <button className="btn btn-primary" >skeleton</button>
                        :
                        <button className="btn btn-outline-primary" onClick={(e) => {
                            setModalTitle("組織登録")
                            setSelectedTenant(undefined)
                            setModalShow(true)
                            setOrgId("")
                            setOrgName("")
                        }} >新規登録</button>
                }
            </div>

        return (
            <>
                {firstDiv}
                {secondDiv}
                {thirdDiv}
                {fourthDiv}
            </>
        )
    }

    const createTableElement = (isSkeleton) => {
        return (
            <div className={"table-responsive" + (isSkeleton ? " skeleton-1" : "") }>
                <table className="table table-striped" style={{visibility: isSkeleton ? "hidden" : "visible"}}>
                    <thead style={{ borderTopWidth: 1 }}>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">組織ID</th>
                            <th scope="col">組織名</th>
                            <th scope="col">アクション</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            isSkeleton ?
                                <>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                </>
                                :
                                createTenantElementList()
                        }
                    </tbody>
                </table>
            </div>

        )
    }

    return (
        pageLoading ?
            <div className="p-3" >
                <div>
                    <div className="row ms-0 me-0 mb-3 skeleton-1" >
                        {createFormElement(true)}
                    </div>
                </div>
                {createTableElement(true)}
            </div>
            :
            <div className="p-3" >
                <form onSubmit={onSearchFormSubmit}>
                    <div className="row mb-3" >
                        {createFormElement()}
                    </div>
                </form>
                {createTableElement()}
                <Modal size='lg' show={modalShow} onHide={() => setModalShow(false)} aria-labelledby='contained-modal-title-vcenter' centered >
                    <div className='ModalModal'>
                        <Modal.Header closeButton>
                            <Modal.Title>{modalTitle}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {
                                createModalBody()
                            }
                        </Modal.Body>
                        <Modal.Footer>
                            {
                                createModalFooter()
                            }
                        </Modal.Footer>
                    </div>
                </Modal>

            </div>
    );
}
